// Live Google Places reviews fetcher.
// Pulls up to 5 most recent reviews via Cloudflare Worker proxy → Google API.
// Caches in localStorage for 24h. Falls back to real reviews if proxy fails.

// Base URL voor de Cloudflare Worker proxy. We voegen een hourly cache-bust
// query-param toe (?h=YYYYMMDD-HH) zodat de Cloudflare-CDN edges elk uur
// een verse fetch naar Google doen ipv de 24u-cache te serveren. Reviews
// updaten weken in plaats van uur, dus 1x per uur fresh is meer dan genoeg.
const PROXY_BASE = "https://abc-reviews-proxy.infoabcongedierte.workers.dev";
function getProxyUrl() {
  const d = new Date();
  const hourBucket = `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}-${String(d.getUTCHours()).padStart(2, '0')}`;
  return `${PROXY_BASE}?h=${hourBucket}`;
}
const PROXY_URL = PROXY_BASE; // back-compat for any usage that doesn't bust cache
// Cache-keys: bij elke bump worden oude caches automatisch verworpen.
// Bump als (a) data-shape verandert, of (b) we vermoeden stale data
// in browser-caches na een live-fix (bv. CSP-issue waardoor cache met
// fallback-data was vastgelopen).
const CACHE_KEY = "abc_google_reviews_v4";
const AGG_CACHE_KEY = "abc_google_aggregate_v3";
// 1 uur browser-cache (was 24u). Worker-URL bust ook elk uur (zie getProxyUrl)
// dus na 1 uur krijgen alle bezoekers verse data van Google.
const CACHE_TTL_MS = 60 * 60 * 1000;

// Fallback reviews (REAL data from Google as of 16 May 2026).
// Kept as backup so the site never appears empty if the proxy fails.
export const GOOGLE_REVIEW_FALLBACK = [
  {
    name: "Laurens Grootveld",
    stars: 5,
    time: "in de afgelopen week",
    text: "Wij zijn ontzettend tevreden over ABC Ongedierte Bestrijding in Den Haag. Vanaf het eerste contact was de communicatie snel, duidelijk en vriendelijk. Hij heeft ons geholpen met muizenproblemen in twee verschillende huizen en beide keren was de service echt uitstekend. Dankzij zijn hulp zijn we eindelijk van de muizenoverlast af. Absoluut een aanrader!",
    profilePhoto: null,
    locality: "Den Haag",
  },
  {
    name: "Ivo Jonker",
    stars: 5,
    time: "in de afgelopen week",
    text: "We zijn heel snel geholpen. Na inspectie van het hele huis zijn gaten onder het keukenblok afgedicht. We zijn zeer tevreden.",
    profilePhoto: null,
    locality: "Den Haag",
  },
  {
    name: "Medha Garg",
    stars: 5,
    time: "in de afgelopen week",
    text: "We hebben een zeer goede ervaring met ze gehad. Ze hebben ons geholpen alle openingen af te dichten om te voorkomen dat er muizen ons huis binnenkomen. Ze waren erg proactief en vriendelijk. We zijn er erg blij mee.",
    profilePhoto: null,
    locality: "Den Haag",
  },
  {
    name: "Lisa De Voogd",
    stars: 5,
    time: "een week geleden",
    text: "Snelle en betrouwbare service bij wespenbestrijding in Den Haag. Binnen korte tijd geholpen en het wespennest vakkundig verwijderd. Duidelijke communicatie, eerlijke prijs en garantie op de behandeling. Zeker een aanrader als je snel van wespen af wilt.",
    profilePhoto: null,
    locality: "Den Haag",
  },
  {
    name: "L. V.",
    stars: 5,
    time: "een week geleden",
    text: "Top geholpen door Jelle van ABC Ongediertebestrijding met wespenbestrijding in Den Haag! Na heldere communicatie was Jelle binnen 24 uur ter plaatse en werd het wespennest vakkundig weggehaald. Service inclusief garantie. Fijn dat er nog vakmensen zijn die een eerlijke prijs rekenen!",
    profilePhoto: null,
    locality: "Den Haag",
  },
];

// Back-compat alias — both names were used across the codebase before the refactor.
export const GOOGLE_REVIEWS_FALLBACK = GOOGLE_REVIEW_FALLBACK;

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.ts || !obj.data) return null;
    if (Date.now() - obj.ts > CACHE_TTL_MS) return null;
    return obj.data;
  } catch (e) {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch (e) { /* quota or disabled — ignore */ }
}

// Filter voor reviews die we niet willen tonen:
//   - lege of whitespace-only review-text (geen bruikbare quote)
//   - placeholder-naam ("K_K K_K", "Anoniem" etc. zonder text)
// Voorkomt lege kaarten in de slider en kopieert deze regel op één plek.
export function isValidReview(r) {
  if (!r) return false;
  const text = (r.text || "").trim();
  if (!text) return false;
  const name = (r.name || "").trim();
  // Placeholder-namen die Google soms teruggeeft voor anonieme reviewers
  if (/^([A-Z]_[A-Z]\s?)+$/i.test(name)) return false;
  return true;
}

// Map a Google PlaceDetails review to our internal shape.
function mapGoogleReview(r) {
  const cities = ["Den Haag", "Rotterdam", "Leiden", "Delft", "Dordrecht", "Zoetermeer",
                  "Schiedam", "Vlaardingen", "Gouda", "Capelle", "Spijkenisse", "Alphen",
                  "Amsterdam", "Haarlem", "Utrecht", "Amersfoort"];
  let locality = null;
  const text = r.text || "";
  for (const c of cities) {
    if (text.includes(c)) { locality = c; break; }
  }
  return {
    name: r.author_name || "Anoniem",
    stars: r.rating || 5,
    text: text,
    time: r.relative_time_description || "",
    profilePhoto: r.profile_photo_url || null,
    locality: locality,
  };
}

let inflight = null;

export function getGoogleReviews() {
  if (inflight) return inflight;

  const cached = readCache();
  if (cached && cached.length) return Promise.resolve(cached);

  inflight = fetch(getProxyUrl())
    .then(r => r.json())
    .then(json => {
      if (json.status !== "OK" || !json.result) {
        console.warn("[Google reviews] proxy returned:", json.status, json.error_message || "");
        return GOOGLE_REVIEW_FALLBACK;
      }
      // Filter weg: lege/whitespace-only review-bodies én anonieme stubs
      // (één klant in onze data heeft naam "K_K K_K" + lege text). Geeft
      // niets weer aan bezoekers ipv een halve kaart.
      const reviews = (json.result.reviews || [])
        .map(mapGoogleReview)
        .filter(isValidReview);
      if (!reviews.length) return GOOGLE_REVIEW_FALLBACK.filter(isValidReview);
      writeCache(reviews);
      return reviews;
    })
    .catch(err => {
      console.warn("[Google reviews] fetch failed:", err);
      return GOOGLE_REVIEW_FALLBACK;
    })
    .finally(() => { inflight = null; });

  return inflight;
}

// Back-compat alias used in some components.
export { getGoogleReviews as fetchGoogleReviews };

// ─────────────────────────────────────────────────────────────────────
// Live aggregate (rating + total) — voor de rating-bar in elke hero.
// Worker returnt `rating` + `user_ratings_total` rechtstreeks uit
// Google Places. We cachen 24u in localStorage zodat we niet bij elke
// page-load fetchen.
// ─────────────────────────────────────────────────────────────────────
function readAggCache() {
  try {
    const raw = localStorage.getItem(AGG_CACHE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.ts || !obj.data) return null;
    if (Date.now() - obj.ts > CACHE_TTL_MS) return null;
    return obj.data;
  } catch (e) {
    return null;
  }
}
function writeAggCache(data) {
  try { localStorage.setItem(AGG_CACHE_KEY, JSON.stringify({ ts: Date.now(), data })); } catch (e) {}
}

let aggInflight = null;

// Returns Promise<{ rating: number, total: number }> or null on failure.
// Caller is expected to fall back to REVIEWS constant from data.js.
export function getGoogleAggregate() {
  if (aggInflight) return aggInflight;
  const cached = readAggCache();
  if (cached) return Promise.resolve(cached);

  aggInflight = fetch(getProxyUrl())
    .then(r => r.json())
    .then(json => {
      if (json.status !== "OK" || !json.result) return null;
      const data = {
        rating: typeof json.result.rating === 'number' ? json.result.rating : null,
        total:  typeof json.result.user_ratings_total === 'number' ? json.result.user_ratings_total : null,
      };
      if (data.rating == null || data.total == null) return null;
      writeAggCache(data);
      return data;
    })
    .catch(() => null)
    .finally(() => { aggInflight = null; });

  return aggInflight;
}
